/*
 * Copyright (c) 2023-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { BrowserHelper } from '@accelbyte/sdk'
import crypto from 'crypto-js'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'

const CSRF_LOCAL_STORAGE_KEY = 'pp:pkce:cd'

const CodeChallengeStoredState = z.object({
  csrf: z.string(),
  codeVerifier: z.string()
})
interface CodeChallengeStoredState extends z.infer<typeof CodeChallengeStoredState> {}

const CodeChallengeSentState = z.object({
  csrf: z.string().uuid(),
  payload: z.nullable(z.string())
})
export interface CodeChallengeSentState extends z.infer<typeof CodeChallengeSentState> {}

export class CodeExchangeCsrfHelpers {
  // Sending CSRF methods.
  static generateCsrf = () => uuidv4()

  static generateChallenge = () => {
    const verifier = base64URLEncode(crypto.lib.WordArray.random(43))
    const challenge = base64URLEncode(crypto.SHA256(verifier))
    return { verifier, challenge }
  }

  static saveCsrfToLocalStorage(codeVerifier: CodeChallengeStoredState) {
    if (BrowserHelper.isOnBrowser()) {
      localStorage.setItem(CSRF_LOCAL_STORAGE_KEY, JSON.stringify(codeVerifier))
    }
  }

  static clearCsrfKeyInLocalStorage = () => {
    if (BrowserHelper.isOnBrowser()) {
      localStorage.removeItem(CSRF_LOCAL_STORAGE_KEY)
    }
  }

  // Parsing/receiving CSRF methods.
  static matchReceivedState = (
    maybeSentState: string
  ): {
    error: unknown | null
    result: { payload: string; codeVerifier: string } | null
  } => {
    const sentStateResult = parseSentState(maybeSentState)
    if (sentStateResult.error) return { error: sentStateResult.error, result: null }

    const storedStateResult = loadStoredState()
    if (storedStateResult.error) return { error: storedStateResult.error, result: null }

    const sentState = sentStateResult.sentState
    const storedState = storedStateResult.storedState

    if (storedState === null || sentState.csrf !== storedState.csrf) return { error: null, result: null }

    return {
      error: null,
      result: {
        payload: sentState.payload,
        codeVerifier: storedState.codeVerifier
      }
    }
  }
}

// Helper functions.
function base64URLEncode(code: crypto.lib.WordArray) {
  return crypto.enc.Base64.stringify(code).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function loadStoredState() {
  const value = localStorage.getItem(CSRF_LOCAL_STORAGE_KEY) ?? ''

  try {
    const parsed = JSON.parse(value)
    return {
      storedState: CodeChallengeStoredState.parse(parsed),
      error: null
    }
  } catch (error) {
    console.error(error)
    return { storedState: null, error }
  }
}

function parseSentState(stringifiedSentState: string) {
  try {
    const parsed = JSON.parse(stringifiedSentState)
    if (!CodeChallengeSentState.parse(parsed)) throw new Error(parsed)
    return {
      sentState: parsed,
      error: null
    }
  } catch (error) {
    console.error(error)
    return { sentState: null, error }
  }
}
